<script lang="ts">
	import { onMount } from 'svelte';
	import { Spinner } from 'flowbite-svelte';
	import { getStatusSummary } from '../AppNetwork';
	import type { StatusSummary } from '../AppTypes';
	import StatusBanner from './public/StatusBanner.svelte';
	import ServiceStatusRow from './public/ServiceStatusRow.svelte';
	import IncidentCard from './public/IncidentCard.svelte';

	let summary: StatusSummary | null = null;
	let loading = true;
	let error = '';

	async function load() {
		loading = true;
		error = '';
		try {
			summary = await getStatusSummary();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load status';
		} finally {
			loading = false;
		}
	}

	onMount(load);
</script>

{#if loading}
	<div class="flex justify-center py-20"><Spinner size="8" /></div>
{:else if error}
	<div class="text-center py-20 text-gray-500">{error}</div>
{:else if summary}
	<div class="space-y-8">
		<StatusBanner status={summary.overall} />

		{#if summary.activeIncidents.length > 0}
			<section class="space-y-3">
				<h2 class="text-sm font-semibold uppercase tracking-wide text-gray-500">Active Incidents</h2>
				{#each summary.activeIncidents as incident}
					<IncidentCard {incident} />
				{/each}
			</section>
		{/if}

		{#each summary.groups as group}
			<section class="rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden">
				<header class="px-4 py-3 bg-gray-50 dark:bg-zinc-800/60 border-b border-gray-200 dark:border-zinc-700">
					<h2 class="font-semibold text-gray-900 dark:text-white">{group.name}</h2>
					{#if group.description}<p class="text-xs text-gray-500">{group.description}</p>{/if}
				</header>
				<div class="divide-y divide-gray-100 dark:divide-zinc-800">
					{#each group.services as service}
						<ServiceStatusRow {service} />
					{/each}
					{#if group.services.length === 0}
						<p class="px-4 py-3 text-sm text-gray-400">No services in this group.</p>
					{/if}
				</div>
			</section>
		{/each}

		{#if summary.ungrouped.length > 0}
			<section class="rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden">
				{#if summary.groups.length > 0}
					<header class="px-4 py-3 bg-gray-50 dark:bg-zinc-800/60 border-b border-gray-200 dark:border-zinc-700">
						<h2 class="font-semibold text-gray-900 dark:text-white">Other Services</h2>
					</header>
				{/if}
				<div class="divide-y divide-gray-100 dark:divide-zinc-800">
					{#each summary.ungrouped as service}
						<ServiceStatusRow {service} />
					{/each}
				</div>
			</section>
		{/if}

		{#if summary.groups.length === 0 && summary.ungrouped.length === 0}
			<p class="text-center text-gray-400 py-10">No services are being monitored yet.</p>
		{/if}
	</div>
{/if}
